/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { Button, Card, message, Space } from 'antd';
import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { KycPresetsSpecFragment } from '../../../api/fragments/KYCPresetsSpec';
import { type ModuleSpecsFragment } from '../../../api/fragments/ModuleSpecs';
import {
    type UpdateKycPresetMutation,
    type UpdateKycPresetMutationVariables,
    UpdateKycPresetDocument,
} from '../../../api/mutations/updateKYCPreset';
import { type KycPresetSettings, type ConditionSettings, type KycFieldSettings } from '../../../api/types';
import Form from '../../../components/fields/Form';
import NotFoundResult from '../../../components/results/NotFoundResult';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import ConditionsDisplay from '../ConsentsAndDeclarationsDetailsPage/Conditions/ConditionsDisplay';
import getConditionSettings from '../ConsentsAndDeclarationsDetailsPage/getConditionSettings';
import prepareSubmissionConditionSettings from '../ConsentsAndDeclarationsDetailsPage/prepareSubmissionConditionSettings';
import KYCFieldsTable from './KYCFieldsTable';
import KYCPresetInfo from './KYCPresetInfo';

export type FormValues = KycPresetSettings & {
    fields: KycFieldSettings[];
    conditionSettings: ConditionSettings[];
};

export type UpdateKycPresetInnerPageProps = {
    module: ModuleSpecsFragment;
    kycPreset: KycPresetsSpecFragment;
};

const UpdateKycPresetInnerPage = ({ module, kycPreset }: UpdateKycPresetInnerPageProps) => {
    const moduleId = module.id;
    const kycPresetId = kycPreset.id;

    const { t } = useTranslation(['localCustomerModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const conditions = useMemo(
        () => (kycPreset.conditions ? getConditionSettings(kycPreset.conditions) : []),
        [kycPreset.conditions]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            displayName: kycPreset.displayName,
            isActive: kycPreset.isActive,
            fields: kycPreset.fields,
            conditionSettings: conditions,
        }),
        [conditions, kycPreset]
    );

    const presetFieldsValidator = useValidator(
        validators.compose(validators.forEach('fields', validators.compose(validators.requiredArray('purpose'))))
    );

    const onSubmit = useHandleError(
        async ({ fields, conditionSettings: conditionSettingsInput, ...values }: FormValues) => {
            const conditionSettings = prepareSubmissionConditionSettings(conditionSettingsInput);

            message.loading({
                content: t('localCustomerModuleDetails:kycPresetsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateKycPresetMutation, UpdateKycPresetMutationVariables>({
                    mutation: UpdateKycPresetDocument,
                    variables: { moduleId, settings: values, fields, kycPresetId, conditionSettings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('localCustomerModuleDetails:kycPresetsDrawer.messages.success'),
                key: 'primary',
            });

            // go back to module detail page
            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, navigate, moduleId, kycPresetId]
    );

    if (module.__typename !== 'LocalCustomerManagementModule') {
        return <NotFoundResult />;
    }

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="kycFieldsForm" htmlType="submit" type="primary">
                    {t('localCustomerModuleDetails:addKYCFields.actions.update')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('localCustomerModuleDetails:addKYCFields.title')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={presetFieldsValidator}>
                {({ handleSubmit }) => (
                    <Form id="kycFieldsForm" name="kycFieldsForm" onSubmitCapture={handleSubmit}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Card title={t('localCustomerModuleDetails:addKYCFields.card.kycPresetsInfo')}>
                                <KYCPresetInfo />
                            </Card>
                            <Card title={t('localCustomerModuleDetails:addKYCFields.card.kycFieldsConfig')}>
                                <KYCFieldsTable module={module} name="fields" />
                            </Card>
                            <Card title={t('localCustomerModuleDetails:addKYCFields.card.conditions')}>
                                <ConditionsDisplay module={module} name="conditionSettings" />
                            </Card>
                        </Space>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateKycPresetInnerPage;
